import { useState, ReactNode } from 'react';
import { Adsense } from '@ctrl/react-adsense';
import withTracker from './withTracker';
import CookieConsent from "react-cookie-consent";
import { BrowserRouter, Route, Link, Routes, useLocation } from 'react-router';
import { Beer, Calculator, ArrowLeftRight, Mail, Menu, ChevronDown, ChevronRight } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

import Home from './Home';
import Contact from "./Contact";
import Carbonation from "./Carbonation";
import Alcohol from "./Alcohol";
import Dilution from "./Dilution";
import Refractometer from "./Refractometer";
import Infusion from "./Infusion";
import HydrometerCorrection from './HydrometerCorrection';
import Temperature from './Temperature';
import Weight from './Weight';
import Volume from './Volume';
import Gravity from './Gravity';
import Recipe from './Recipe';
import BeerStyles from './BeerStyles';
import BeerStyleDetail from './BeerStyleDetail';

const TrackedHome = withTracker(Home);
const TrackedContact = withTracker(Contact);
const TrackedCarbonation = withTracker(Carbonation);
const TrackedAlcohol = withTracker(Alcohol);
const TrackedDilution = withTracker(Dilution);
const TrackedRefractometer = withTracker(Refractometer);
const TrackedInfusion = withTracker(Infusion);
const TrackedHydrometerCorrection = withTracker(HydrometerCorrection);
const TrackedTemperature = withTracker(Temperature);
const TrackedWeight = withTracker(Weight);
const TrackedVolume = withTracker(Volume);
const TrackedGravity = withTracker(Gravity);
const TrackedRecipe = withTracker(Recipe);
const TrackedBeerStyles = withTracker(BeerStyles);
const TrackedBeerStyleDetail = withTracker(BeerStyleDetail);

interface NavLinkProps {
  to: string;
  icon?: LucideIcon;
  children: ReactNode;
  onClick?: () => void;
}

function NavLink({ to, icon: Icon, children, onClick }: NavLinkProps) {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <Link
      to={to}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
        isActive
          ? "bg-primary text-primary-foreground"
          : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      )}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      {children}
    </Link>
  );
}

interface CollapsibleNavProps {
  icon?: LucideIcon;
  label: string;
  children: ReactNode;
}

function CollapsibleNav({ icon: Icon, label, children }: CollapsibleNavProps) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
      >
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        <span className="flex-1 text-left">{label}</span>
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>
      {open && (
        <div className="ml-7 mt-1 flex flex-col gap-0.5">
          {children}
        </div>
      )}
    </div>
  );
}

interface SubNavLinkProps {
  to: string;
  children: ReactNode;
  onClick?: () => void;
}

function SubNavLink({ to, children, onClick }: SubNavLinkProps) {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <Link
      to={to}
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 rounded-md text-sm transition-colors",
        isActive
          ? "bg-primary text-primary-foreground font-medium"
          : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      )}
    >
      {children}
    </Link>
  );
}

interface SidebarContentProps {
  onNavigate?: () => void;
}

function SidebarContent({ onNavigate }: SidebarContentProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col h-full">
      <Link
        to="/"
        onClick={onNavigate}
        className="flex items-center gap-2 px-4 py-5 border-b border-sidebar-border"
      >
        <div className="flex items-center gap-1 text-primary">
          <Beer className="h-5 w-5 rotate-12" />
          <Beer className="h-5 w-5 -rotate-12" />
        </div>
        <span className="font-semibold text-sidebar-foreground text-sm leading-tight">
          {t('common.appName')}
        </span>
      </Link>

      <nav className="flex-1 p-3 flex flex-col gap-1 overflow-y-auto">
        <NavLink to="/" icon={Beer} onClick={onNavigate}>
          {t('nav.home')}
        </NavLink>
        <NavLink to="/recipe" icon={Calculator} onClick={onNavigate}>
          {t('nav.recipe')}
        </NavLink>
        <NavLink to="/styles" icon={Beer} onClick={onNavigate}>
          {t('nav.styles')}
        </NavLink>

        <CollapsibleNav icon={Calculator} label={t('nav.calculators')}>
          <SubNavLink to="/carbonation" onClick={onNavigate}>{t('nav.carbonation')}</SubNavLink>
          <SubNavLink to="/dilution" onClick={onNavigate}>{t('nav.dilution')}</SubNavLink>
          <SubNavLink to="/alcohol" onClick={onNavigate}>{t('nav.alcohol')}</SubNavLink>
          <SubNavLink to="/refractometer" onClick={onNavigate}>{t('nav.refractometer')}</SubNavLink>
          <SubNavLink to="/infusion" onClick={onNavigate}>{t('nav.infusion')}</SubNavLink>
          <SubNavLink to="/hydrometer-correction" onClick={onNavigate}>{t('nav.hydrometerCorrection')}</SubNavLink>
        </CollapsibleNav>

        <CollapsibleNav icon={ArrowLeftRight} label={t('nav.converters')}>
          <SubNavLink to="/weight" onClick={onNavigate}>{t('nav.weight')}</SubNavLink>
          <SubNavLink to="/temperature" onClick={onNavigate}>{t('nav.temperature')}</SubNavLink>
          <SubNavLink to="/volume" onClick={onNavigate}>{t('nav.volume')}</SubNavLink>
          <SubNavLink to="/gravity" onClick={onNavigate}>{t('nav.gravity')}</SubNavLink>
        </CollapsibleNav>

        <NavLink to="/contact" icon={Mail} onClick={onNavigate}>
          {t('nav.contact')}
        </NavLink>
      </nav>

      <div className="border-t border-sidebar-border">
        <LanguageSwitcher />
      </div>
    </div>
  );
}

function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="flex h-screen bg-background">
      <aside className="hidden md:flex w-60 shrink-0 flex-col bg-sidebar border-r border-sidebar-border">
        <SidebarContent />
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-14 border-b bg-card flex items-center px-4 gap-4 shrink-0">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="md:hidden p-1.5 rounded-md hover:bg-accent transition-colors">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-60 bg-sidebar border-sidebar-border">
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-1.5">
            <Beer className="h-5 w-5 text-primary" />
            <span className="font-semibold text-sm">{t('common.appName')}</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-1">
              <Adsense
                client='ca-pub-4891795862700093'
                slot='2021917566'
              />
            </div>
            <Routes>
              <Route path="/" element={<TrackedHome />} />
              <Route path="/contact" element={<TrackedContact />} />
              <Route path="/carbonation" element={<TrackedCarbonation />} />
              <Route path="/alcohol" element={<TrackedAlcohol />} />
              <Route path="/dilution" element={<TrackedDilution />} />
              <Route path="/refractometer" element={<TrackedRefractometer />} />
              <Route path="/infusion" element={<TrackedInfusion />} />
              <Route path="/temperature" element={<TrackedTemperature />} />
              <Route path="/weight" element={<TrackedWeight />} />
              <Route path="/volume" element={<TrackedVolume />} />
              <Route path="/gravity" element={<TrackedGravity />} />
              <Route path="/recipe" element={<TrackedRecipe />} />
              <Route path="/styles" element={<TrackedBeerStyles />} />
              <Route path="/styles/:id" element={<TrackedBeerStyleDetail />} />
              <Route path="/hydrometer-correction" element={<TrackedHydrometerCorrection />} />
            </Routes>
          </div>
        </main>
      </div>

      <CookieConsent
        location="bottom"
        buttonText={t('cookie.accept')}
        cookieName="cookieLawSeen"
        style={{ background: "#1c1917", fontSize: '13px' }}
        buttonStyle={{
          background: '#f59e0b',
          color: '#1c1917',
          fontSize: '13px',
          borderRadius: '6px',
          fontWeight: '600',
          padding: '6px 16px',
        }}
        expires={150}
      >
        <p>{t('cookie.message')}</p>
        <p style={{ fontSize: '11px', opacity: 0.8, marginTop: '4px' }}>
          {t('cookie.description')}
        </p>
      </CookieConsent>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppLayout />
    </BrowserRouter>
  );
}
